import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PreviewOverlayPortalProps {
  visible: boolean;
  pixels: number[][] | null;
  colors: string[];
  scale?: number;
  secondaryColors?: string[];
}

// Minimal portal-overlay, ritar alltid 16×16 med stöd för secondary
const PreviewOverlayPortal: React.FC<PreviewOverlayPortalProps> = ({
  visible,
  pixels,
  colors,
  scale = 8,
  secondaryColors,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;

    const s = scale ?? 8;
    const w = 16 * s;
    const h = 16 * s;

    cv.width = w;
    cv.height = h;
    cv.style.width = w + 'px';
    cv.style.height = h + 'px';

    const ctx = cv.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    // Svart bakgrund
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    // Färgupplösning med stöd för 128..143 och -16..-1
    const resolve = (ci: number) => {
      const isSec = (ci & 0x80) !== 0 || ci < 0;
      const base = ci < 0 ? (ci + 16) & 0x0f : (ci & 0x0f);
      const sec = secondaryColors ?? colors;
      return isSec ? (sec[base] ?? colors[base] ?? '#000') : (colors[base] ?? '#000');
    };

    // Rita alltid 16×16; 0 är transparent (visar svarta bakgrunden)
    for (let y = 0; y < 16; y++) {
      const row = pixels?.[y];
      for (let x = 0; x < 16; x++) {
        const ci = row?.[x] ?? 0;
        if (ci === 0) continue; // endast exakt 0 är transparent; secondary med base 0 ska ritas
        ctx.fillStyle = resolve(ci);
        ctx.fillRect(x * s, y * s, s, s);
      }
    }
  }, [pixels, colors, scale, secondaryColors]);

  const size = 16 * (scale ?? 8);
  const node = (
    <div
      className="preview-overlay"
      style={{
        position: 'fixed',
        top: '12px',
        right: '12px',
        zIndex: 99999,
        borderRadius: 8,
        padding: 6,
        background: 'rgba(0,0,0,0.55)',
        opacity: visible ? 1 : 0, // synlighet via opacity, inte unmount
        transition: 'opacity 120ms ease',
        pointerEvents: 'none',
        isolation: 'isolate',
        willChange: 'opacity, transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        width: size,
        height: size,
        overflow: 'visible',
      }}
    >
      <div style={{ width: '100%', height: '100%' }}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', height: '100%', imageRendering: 'pixelated' as any }}
        />
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default PreviewOverlayPortal;